import { useEffect, useState } from "react";
import { Save } from "@/components/icons";
import { toast } from "sonner";
import api, { formatErr } from "../../../lib/api";
import { Card, Input, Select, Textarea, DatePicker, sanitizePatient } from "./common";
import AddressAutocomplete from "../../../components/AddressAutocomplete";

export default function FullInfoTab({ patient, onSaved }) {
  const [form, setForm] = useState(() => ({ ...sanitizePatient(patient) }));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pharmacies, setPharmacies] = useState([]);

  useEffect(() => {
    api
      .get("/users/pharmacies")
      .then(({ data }) => setPharmacies(data || []))
      .catch(() => {});
  }, []);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/patients/${patient.id}`, form);
      toast.success("Información actualizada");
      setDirty(false);
      onSaved();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[13px] font-semibold text-black">
          Información completa del paciente
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="patient-fullinfo-save"
          className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-lg text-[13px] font-semibold transition ${
            dirty
              ? "bg-black hover:bg-black/85 text-white"
              : "bg-[#F2F3F5] text-black/40 cursor-not-allowed"
          }`}
        >
          <Save className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input
          label="Nombre completo"
          value={form.full_name}
          onChange={(v) => set("full_name", v)}
        />
        <Input
          label="Documento"
          value={form.document_id}
          onChange={(v) => set("document_id", v)}
        />
        <DatePicker
          label="Fecha de nacimiento"
          value={form.birth_date}
          onChange={(v) => set("birth_date", v)}
        />
        <Select
          label="Género"
          value={form.gender}
          onChange={(v) => set("gender", v)}
          options={[
            { v: "", l: "—" },
            { v: "male", l: "Masculino" },
            { v: "female", l: "Femenino" },
            { v: "other", l: "Otro" },
          ]}
        />
        <Input label="Teléfono" value={form.phone} onChange={(v) => set("phone", v)} />
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(v) => set("email", v)}
        />
        <Select
          label="Tipo de sangre"
          value={form.blood_type}
          onChange={(v) => set("blood_type", v)}
          options={[
            { v: "", l: "—" },
            ...["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((b) => ({
              v: b,
              l: b,
            })),
          ]}
        />
        <Input
          label="Alergias"
          value={form.allergies}
          onChange={(v) => set("allergies", v)}
        />
        <div className="sm:col-span-2">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1">
              Dirección
            </span>
            <AddressAutocomplete
              value={form.address}
              onChange={(v) => set("address", v)}
              showIcon={false}
              inputClassName="w-full h-10 px-3 rounded-lg border border-black/[0.08] bg-white text-[13px] text-black focus:outline-none focus:border-black/40 transition"
              testid="fullinfo-address"
            />
          </label>
        </div>
        <div className="sm:col-span-2">
          <Textarea
            label="Notas generales"
            value={form.notes}
            onChange={(v) => set("notes", v)}
            rows={3}
          />
        </div>
        <div className="sm:col-span-2">
          <Select
            label="Farmacia predeterminada"
            value={form.preferred_pharmacy_id || ""}
            onChange={(v) => set("preferred_pharmacy_id", v || null)}
            options={[
              { v: "", l: "— Sin farmacia predeterminada —" },
              ...pharmacies.map((p) => ({
                v: p.id,
                l: p.name || p.email,
              })),
            ]}
          />
          <p className="text-[11px] text-black/45 mt-1.5 leading-relaxed">
            Se pre-seleccionará automáticamente al emitir una nueva receta a
            este paciente. Puedes cambiarla en cualquier momento.
          </p>
        </div>
      </div>
    </Card>
  );
}
